<template>
  <div class="team-page">
    <div class="team-hero">
      <div class="team-container team-hero-inner">
        <div>
          <p class="team-kicker">Workspace access</p>
          <h1 class="team-title">Team</h1>
          <p class="team-subtitle">Manage who has access to this workspace</p>
        </div>
        <button
          v-if="isAdmin"
          @click="refreshAll"
          class="team-btn team-btn-ghost"
          data-testid="team-refresh"
        >
          Refresh
        </button>
      </div>
    </div>

    <div class="team-container team-content">

      <!-- Non-admin gate ---------------------------------------------------->
      <div
        v-if="!isAdmin"
        class="team-card"
        data-testid="team-non-admin-gate"
        role="status"
      >
        <h2 class="team-card-title">Read-only view</h2>
        <p class="team-muted">
          Only tenant admins can invite teammates or change membership.
          Contact a workspace admin if you need to make changes.
        </p>
      </div>

      <!-- Admin: invite form ----------------------------------------------->
      <div
        v-if="isAdmin"
        class="team-card"
        data-testid="team-invite-card"
      >
        <h2 class="team-card-title team-card-title-spaced">Invite teammate</h2>
        <form @submit.prevent="submitInvite" class="team-form">
          <div class="team-form-grid">
            <div>
              <label class="team-label" for="invite-email">Email</label>
              <input
                id="invite-email"
                v-model="inviteForm.email"
                type="email"
                required
                class="team-input"
                data-testid="invite-email"
                placeholder="teammate@company.com"
              />
            </div>
            <div>
              <label class="team-label" for="invite-role">Role</label>
              <select
                id="invite-role"
                v-model="inviteForm.role"
                class="team-input"
                data-testid="invite-role"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label class="team-label" for="invite-name">Display name (optional)</label>
              <input
                id="invite-name"
                v-model="inviteForm.name"
                type="text"
                class="team-input"
                data-testid="invite-name"
                placeholder="Jamie Wright"
              />
            </div>
          </div>
          <div
            v-if="inviteError"
            class="team-alert team-alert-danger"
            data-testid="invite-error"
            role="alert"
          >{{ inviteError }}</div>
          <div class="team-form-actions">
            <button
              type="submit"
              :disabled="inviteSubmitting"
              class="team-btn team-btn-accent"
              data-testid="invite-submit"
            >
              {{ inviteSubmitting ? 'Sending invite…' : 'Send invite' }}
            </button>
            <p
              v-if="inviteSuccess"
              class="team-success"
              data-testid="invite-success"
            >{{ inviteSuccess }}</p>
          </div>
        </form>
      </div>

      <!-- Active members --------------------------------------------------->
      <div class="team-card team-card-table">
        <div class="team-card-header">
          <h2 class="team-card-title">Active members</h2>
          <p class="team-muted">People with access to this workspace</p>
        </div>
        <div v-if="loading" class="team-empty-row" data-testid="team-loading">Loading…</div>
        <div v-else-if="!members.length" class="team-empty-row" data-testid="team-empty-members">
          No active members yet.
        </div>
        <div v-else class="team-table-wrap">
          <table class="team-table" data-testid="team-members-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Status</th>
                <th v-if="isAdmin" class="team-actions-head">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="member in members"
                :key="member.id"
                class="team-table-row"
                :data-testid="`team-member-row-${member.id}`"
              >
                <td>
                  <div class="team-primary">{{ member.name || member.email }}</div>
                  <div class="team-secondary">{{ member.email }}</div>
                </td>
                <td :data-testid="`team-member-role-${member.id}`">
                  <span :class="roleBadgeClass(member.role)">{{ roleLabel(member.role) }}</span>
                </td>
                <td :data-testid="`team-member-status-${member.id}`">
                  <span :class="statusBadgeClass(member.status)">{{ statusLabel(member.status) }}</span>
                </td>
                <td v-if="isAdmin" class="team-actions-cell">
                  <select
                    :value="member.role"
                    @change="onRoleChange(member, $event.target.value)"
                    class="team-mini-select"
                    :disabled="busyMemberId === member.id"
                    :data-testid="`team-member-role-select-${member.id}`"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button
                    v-if="member.status === 'active'"
                    @click="onStatusChange(member, 'suspended')"
                    :disabled="busyMemberId === member.id"
                    class="team-mini-btn"
                    :data-testid="`team-member-suspend-${member.id}`"
                  >Suspend</button>
                  <button
                    v-else
                    @click="onStatusChange(member, 'active')"
                    :disabled="busyMemberId === member.id"
                    class="team-mini-btn team-mini-btn-success"
                    :data-testid="`team-member-reactivate-${member.id}`"
                  >Reactivate</button>
                  <button
                    @click="onRemove(member)"
                    :disabled="busyMemberId === member.id"
                    class="team-mini-btn team-mini-btn-danger"
                    :data-testid="`team-member-remove-${member.id}`"
                  >Remove</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pending invites -------------------------------------------------->
      <div class="team-card team-card-table">
        <div class="team-card-header">
          <h2 class="team-card-title">Pending invites</h2>
          <p class="team-muted">Invitations that haven’t been accepted yet</p>
        </div>
        <div v-if="loading" class="team-empty-row">Loading…</div>
        <div v-else-if="!invites.length" class="team-empty-row" data-testid="team-empty-invites">
          No pending invites.
        </div>
        <div v-else class="team-table-wrap">
          <table class="team-table" data-testid="team-invites-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Expires</th>
                <th v-if="isAdmin" class="team-actions-head">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="invite in invites"
                :key="invite.id"
                class="team-table-row"
                :data-testid="`team-invite-row-${invite.id}`"
              >
                <td>
                  <div class="team-primary">{{ invite.email }}</div>
                  <div v-if="invite.name" class="team-secondary">{{ invite.name }}</div>
                </td>
                <td>
                  <span :class="roleBadgeClass(invite.role)">{{ roleLabel(invite.role) }}</span>
                </td>
                <td>
                  <span :class="statusBadgeClass('pending')">Pending</span>
                </td>
                <td class="team-secondary" data-testid="team-invite-expiry">
                  {{ formatDate(invite.expiresAt) }}
                </td>
                <td v-if="isAdmin" class="team-actions-cell">
                  <button
                    @click="onCancelInvite(invite)"
                    class="team-mini-btn team-mini-btn-danger"
                    :data-testid="`team-invite-cancel-${invite.id}`"
                  >Cancel invite</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-if="loadError"
        class="team-alert team-alert-danger"
        data-testid="team-load-error"
        role="alert"
      >{{ loadError }}</div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, reactive } from 'vue'
import { useSitesStore } from '../stores/sites'

export default {
  name: 'Team',
  setup() {
    const store = useSitesStore()

    const loading = ref(false)
    const loadError = ref('')
    const inviteSubmitting = ref(false)
    const inviteError = ref('')
    const inviteSuccess = ref('')
    const busyMemberId = ref(null)

    const inviteForm = reactive({ email: '', role: 'member', name: '' })

    const members = computed(() => store.teamMembers || [])
    const invites = computed(() => store.teamInvites || [])
    const isAdmin = computed(() => Boolean(store.user?.isAdmin || store.user?.is_admin))

    const refreshAll = async () => {
      loading.value = true
      loadError.value = ''
      try {
        await store.fetchTeam()
      } catch (err) {
        loadError.value = err?.response?.data?.error || err?.message || 'Failed to load team'
      } finally {
        loading.value = false
      }
    }

    const submitInvite = async () => {
      inviteError.value = ''
      inviteSuccess.value = ''
      inviteSubmitting.value = true
      try {
        await store.inviteTeammate({
          email: inviteForm.email,
          role: inviteForm.role,
          name: inviteForm.name || undefined,
        })
        inviteSuccess.value = `Invitation sent to ${inviteForm.email}`
        inviteForm.email = ''
        inviteForm.name = ''
        inviteForm.role = 'member'
        await refreshAll()
      } catch (err) {
        const data = err?.response?.data || {}
        if (Array.isArray(data.validationErrors) && data.validationErrors.length) {
          inviteError.value = data.validationErrors
            .map((v) => `${v.field || 'field'}: ${v.message}`).join('; ')
        } else {
          inviteError.value = data.error || err?.message || 'Failed to invite teammate'
        }
      } finally {
        inviteSubmitting.value = false
      }
    }

    const onRoleChange = async (member, role) => {
      if (role === member.role) return
      busyMemberId.value = member.id
      try {
        await store.changeTeamRole(member.id, role)
        await refreshAll()
      } catch (err) {
        loadError.value = err?.response?.data?.error || err?.message || 'Failed to change role'
      } finally {
        busyMemberId.value = null
      }
    }

    const onStatusChange = async (member, status) => {
      busyMemberId.value = member.id
      try {
        await store.changeTeamStatus(member.id, status)
        await refreshAll()
      } catch (err) {
        loadError.value = err?.response?.data?.error || err?.message || 'Failed to update status'
      } finally {
        busyMemberId.value = null
      }
    }

    const onRemove = async (member) => {
      if (!window.confirm(`Remove ${member.email} from this workspace?`)) return
      busyMemberId.value = member.id
      try {
        await store.removeTeamMember(member.id)
        await refreshAll()
      } catch (err) {
        loadError.value = err?.response?.data?.error || err?.message || 'Failed to remove member'
      } finally {
        busyMemberId.value = null
      }
    }

    const onCancelInvite = async (invite) => {
      try {
        await store.cancelTeamInvite(invite.invitationId || invite.id)
        await refreshAll()
      } catch (err) {
        loadError.value = err?.response?.data?.error || err?.message || 'Failed to cancel invitation'
      }
    }

    const roleLabel = (role) => (role === 'admin' ? 'Admin' : 'Member')
    const statusLabel = (status) => {
      if (status === 'active') return 'Active'
      if (status === 'suspended') return 'Suspended'
      if (status === 'pending') return 'Pending'
      return status || 'Unknown'
    }
    const roleBadgeClass = (role) => {
      const base = 'team-badge'
      return `${base} ${role === 'admin' ? 'team-badge-admin' : 'team-badge-member'}`
    }
    const statusBadgeClass = (status) => {
      const base = 'team-badge'
      if (status === 'active') return `${base} team-badge-active`
      if (status === 'suspended') return `${base} team-badge-suspended`
      if (status === 'pending') return `${base} team-badge-pending`
      return `${base} team-badge-member`
    }
    const formatDate = (iso) => {
      if (!iso) return '—'
      try { return new Date(iso).toLocaleString() } catch (_) { return iso }
    }

    onMounted(() => {
      if (isAdmin.value) refreshAll()
    })

    return {
      loading,
      loadError,
      members,
      invites,
      isAdmin,
      inviteForm,
      inviteSubmitting,
      inviteError,
      inviteSuccess,
      busyMemberId,
      refreshAll,
      submitInvite,
      onRoleChange,
      onStatusChange,
      onRemove,
      onCancelInvite,
      roleLabel,
      statusLabel,
      roleBadgeClass,
      statusBadgeClass,
      formatDate,
    }
  }
}
</script>

<style scoped>
.team-page {
  min-height: 100vh;
  color: var(--text-primary);
  background:
    radial-gradient(circle at 10% 0%, rgba(124,109,250,0.16), transparent 34rem),
    radial-gradient(circle at 90% 14%, rgba(14,165,233,0.10), transparent 30rem),
    var(--surface-base);
}

.team-container {
  width: min(960px, calc(100% - 32px));
  margin: 0 auto;
}

.team-hero {
  border-bottom: var(--glass-border);
  background: rgba(15,15,24,0.72);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
}

.team-hero-inner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 32px 0 28px;
}

.team-kicker {
  margin: 0 0 8px;
  color: var(--accent);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.team-title {
  margin: 0;
  font-size: clamp(30px, 5vw, 48px);
  font-weight: 750;
  letter-spacing: -0.045em;
}

.team-subtitle,
.team-muted,
.team-secondary {
  color: var(--text-secondary);
}

.team-subtitle {
  margin: 8px 0 0;
  font-size: 15px;
}

.team-content {
  display: grid;
  gap: 22px;
  padding: 28px 0 48px;
}

.team-card {
  padding: 22px;
  border: var(--glass-border);
  border-radius: var(--radius-lg);
  background: var(--glass-bg);
  box-shadow: var(--elev-sm);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
}

.team-card-table {
  padding: 0;
  overflow: hidden;
}

.team-card-header {
  padding: 20px 22px 16px;
  border-bottom: var(--glass-border);
}

.team-card-title {
  margin: 0 0 6px;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.team-card-title-spaced {
  margin-bottom: 18px;
}

.team-muted {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
}

.team-form {
  display: grid;
  gap: 16px;
}

.team-form-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.team-label {
  display: block;
  margin-bottom: 7px;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.team-input,
.team-mini-select {
  width: 100%;
  color: var(--text-primary);
  border: var(--glass-border);
  border-radius: 10px;
  background: var(--glass-bg-active);
  outline: none;
}

.team-input {
  padding: 10px 12px;
  font-size: 14px;
}

.team-input:focus,
.team-mini-select:focus {
  border: var(--glass-border-hi);
  box-shadow: 0 0 0 3px rgba(124,109,250,0.14);
}

.team-form-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.team-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.team-btn-ghost {
  color: var(--text-secondary);
  border: var(--glass-border);
  background: var(--glass-bg);
}

.team-btn-ghost:hover {
  color: var(--text-primary);
  border: var(--glass-border-hi);
  background: var(--glass-bg-hover);
}

.team-btn-accent {
  color: #fff;
  border: 1px solid transparent;
  background: var(--accent);
  box-shadow: 0 0 18px var(--accent-glow);
}

.team-btn:disabled,
.team-mini-btn:disabled,
.team-mini-select:disabled,
.team-input:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.team-alert {
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  font-size: 13px;
}

.team-alert-danger {
  color: #fecaca;
  border: 1px solid rgba(239,68,68,0.28);
  background: rgba(239,68,68,0.12);
}

.team-success {
  color: #86efac;
  font-size: 13px;
}

.team-empty-row {
  padding: 24px 22px;
  color: var(--text-secondary);
  font-size: 14px;
}

.team-table-wrap {
  overflow-x: auto;
}

.team-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.team-table th {
  padding: 12px 22px;
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-align: left;
  text-transform: uppercase;
}

.team-table td {
  padding: 14px 22px;
  vertical-align: middle;
}

.team-table-row {
  border-top: var(--glass-border);
}

.team-table-row:hover {
  background: var(--glass-bg-hover);
}

.team-actions-head,
.team-actions-cell {
  text-align: right !important;
}

.team-actions-cell {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.team-primary {
  color: var(--text-primary);
  font-weight: 700;
}

.team-secondary {
  font-size: 12px;
}

.team-mini-select {
  width: auto;
  padding: 6px 8px;
  font-size: 12px;
}

.team-mini-btn {
  padding: 6px 9px;
  color: var(--text-secondary);
  border: var(--glass-border);
  border-radius: 999px;
  background: var(--glass-bg);
  font-size: 12px;
  font-weight: 700;
}

.team-mini-btn:hover {
  color: var(--text-primary);
  border: var(--glass-border-hi);
  background: var(--glass-bg-hover);
}

.team-mini-btn-success {
  color: #86efac;
}

.team-mini-btn-danger {
  color: #fca5a5;
}

:deep(.team-badge) {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  border: var(--glass-border);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

:deep(.team-badge-admin) {
  color: #c4b5fd;
  background: rgba(124,109,250,0.18);
}

:deep(.team-badge-member) {
  color: var(--text-secondary);
  background: var(--glass-bg-active);
}

:deep(.team-badge-active) {
  color: #86efac;
  background: rgba(34,197,94,0.14);
}

:deep(.team-badge-suspended) {
  color: #fcd34d;
  background: rgba(245,158,11,0.14);
}

:deep(.team-badge-pending) {
  color: #93c5fd;
  background: rgba(59,130,246,0.14);
}

@media (max-width: 780px) {
  .team-form-grid {
    grid-template-columns: 1fr;
  }

  .team-hero-inner {
    flex-direction: column;
  }
}
</style>
